import { useState, useEffect, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import axios from "axios";
import { ProfilePictureUpload } from "../../../components/ProfilePictureUpload/ProfilePictureUpload";
import { getS3PresignedUrl } from "../../../services/api/apiClient";
import { PresignFileType } from "../../../services/api/types/portalConfiguration/uploadPhoto";
import { useToast } from "../../../context/ToastContext";
import { useTranslate } from "../../../hooks/useTranslate";

interface ProfilePictureSectionProps {
  initialThumbnailUrl?: string | null;
  portalUid: string;
  onThumbnailPathChange?: (path: string | null) => void;
}

export const ProfilePictureSection: React.FC<ProfilePictureSectionProps> = ({
  initialThumbnailUrl,
  portalUid,
  onThumbnailPathChange,
}) => {
  const { showToast } = useToast();
  const t = useTranslate("my-profile");
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [uploadedThumbnailPath, setUploadedThumbnailPath] = useState<string | null>(null);
  const uploadedFileObjectUrlRef = useRef<string | null>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  // Initialize profile picture URL from prop
  useEffect(() => {
    if (!uploadedThumbnailPath) {
      setProfilePictureUrl(initialThumbnailUrl || null);
    }
  }, [initialThumbnailUrl, uploadedThumbnailPath]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (uploadedFileObjectUrlRef.current) {
        URL.revokeObjectURL(uploadedFileObjectUrlRef.current);
      }
    };
  }, []);

  const handleProfilePictureUpload = async (file: File | null) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast("Image size must not exceed 5MB.", "error");
      return;
    }

    setUploadingPicture(true);
    try {
      const { uploadUrl, path } = await getS3PresignedUrl(portalUid, {
        fileName: file.name,
        contentType: file.type,
        fileType: PresignFileType.PROFILE_PICTURE,
      });
      
      await axios.put(uploadUrl, file, { headers: { "Content-Type": file.type } });
      
      if (uploadedFileObjectUrlRef.current) {
        URL.revokeObjectURL(uploadedFileObjectUrlRef.current);
      }
      uploadedFileObjectUrlRef.current = URL.createObjectURL(file);
      
      setUploadedThumbnailPath(path);
      setProfilePictureUrl(uploadedFileObjectUrlRef.current);
      onThumbnailPathChange?.(path);
      showToast("Profile picture uploaded successfully", "success");
    } catch (error: any) {
      console.error("Error uploading profile picture:", error);
      showToast(
        error?.response?.data?.message ||
          "Failed to upload profile picture. Please try again.",
        "error"
      );
    } finally {
      setUploadingPicture(false);
    }
  };


  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
      }}
    >
      <ProfilePictureUpload
        value={profilePictureUrl || null}
        onChange={handleProfilePictureUpload}
        maxWidth={800}
        maxHeight={400}
        helperText="SVG, PNG, JPG or GIF (max. 800x400px)"
      />
      {uploadingPicture && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="caption" color="text.secondary">
            {t("page", "uploading")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

